import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, signal, computed, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule } from '@ngx-translate/core';

import { DataTableComponent, TableColumn } from '@shared/components/data-table/data-table.component';
import { Warehouse, Product } from '../../../models/synchronization.model';
import { AppButtonComponent } from "@app/shared/components/app-button/app-button.component";

export type SelectionType = 'warehouse' | 'product';
export type SelectionItem = Warehouse | Product;

export interface SelectionDialogConfig {
  type: SelectionType;
  title: string;
  searchFields: {
    label: string;
    placeholder: string;
    key: string;
  }[];
  tableColumns: TableColumn[];
  width: string;
  maxWidth: string;
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  confirmButtonLabel?: string;
}

@Component({
  selector: 'app-selection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    DataTableComponent,
    TranslateModule,
    AppButtonComponent
],
  templateUrl: './selection-dialog.component.html',
  styleUrls: ['./selection-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectionDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() config: SelectionDialogConfig | null = null;
  @Input() items: SelectionItem[] = [];
  @Input() loading = false;
  @Input() totalRecords = 0;
  @Input() initialSelection: SelectionItem[] = [];
  @Input() disabledSelection: SelectionItem[] = []; // New input for disabled items
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() selectionConfirm = new EventEmitter<SelectionItem[]>();
  @Output() search = new EventEmitter<Record<string, string>>();
  @Output() searchReset = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<number>();

  // Dialog state - using signals for reactive state management
  selectedItems = signal<SelectionItem[]>([]);
  searchForm = signal<Record<string, string>>({});
  currentPage = signal(1);
  pageSize = signal(20);

  // Computed properties
  dialogStyle = computed(() => ({
    width: this.config?.width,
    maxWidth: this.config?.maxWidth,
    height: this.config?.height,
    maxHeight: this.config?.maxHeight
  }));

  constructor() {
    // Simple initialization - no complex effects needed
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle config changes - clear selection when dialog type changes
    if (changes['config'] && this.visible) {
      this.selectedItems.set([]);
    }
    
    // Handle initial selection changes
    if (changes['initialSelection'] && this.visible && this.initialSelection.length > 0) {
      this.setInitialSelection(this.initialSelection);
    }
    
    // Handle items changes - apply initial selection when data is loaded
    if (changes['items'] && this.visible && this.initialSelection.length > 0 && this.items.length > 0) {
      this.setInitialSelection(this.initialSelection);
    }
  }

  onVisibleChange(visible: boolean): void {
    if (visible) {
      // Always clear selection first when dialog opens
      this.selectedItems.set([]);
      
      if (this.initialSelection.length > 0) {
        // Set initial selection when dialog opens
        this.setInitialSelection(this.initialSelection);
      }
    }
    this.visibleChange.emit(visible);
  }

  onSearch(): void {
    this.currentPage.set(1);
    this.search.emit(this.searchForm());
  }

  onSearchReset(): void {
    const resetForm: Record<string, string> = {};
    this.config?.searchFields?.forEach(field => {
      resetForm[field.key] = '';
    });
    this.searchForm.set(resetForm);
    this.currentPage.set(1);
    this.searchReset.emit();
  }

  onPageChange(event: any): void {
    this.currentPage.set(event.page + 1);
    this.pageChange.emit(event.page + 1);
  }

  onSelectionChange(selection: SelectionItem[]): void {
    // For warehouse selection, ensure only one item can be selected
    if (this.config?.type === 'warehouse') {
      // If more than one item is selected, keep only the last one
      if (selection.length > 1) {
        const lastSelected = selection[selection.length - 1];
        this.selectedItems.set([lastSelected]);
      } else {
        this.selectedItems.set(selection);
      }
    } else if (this.config?.type === 'product') {
      // For product selection, filter out disabled items from the selection
      const filteredSelection = selection.filter(item => !this.isItemDisabled(item));
      this.selectedItems.set(filteredSelection);
    } else {
      // For other types, allow multiple selections
      this.selectedItems.set(selection);
    }
  }

  // Method to set initial selection when dialog opens
  setInitialSelection(selection: SelectionItem[]): void {
    this.selectedItems.set(selection);
  }

  // Method to check if an item is disabled (for product selection)
  isItemDisabled(item: SelectionItem): boolean {
    if (this.config?.type === 'product' && this.disabledSelection.length > 0) {
      return this.disabledSelection.some(disabledItem => disabledItem.id === item.id);
    }
    return false;
  }

  // Method to check if an item should appear as selected (including disabled items)
  isItemSelected(item: SelectionItem): boolean {
    // Check if item is in current selection
    const isInSelection = this.selectedItems().some(selectedItem => {
      // For warehouse selection, also match by code if ID is 0 (fallback)
      if (this.config?.type === 'warehouse') {
        return selectedItem.id === item.id || 
               (selectedItem.id === 0 && (selectedItem as Warehouse).code === (item as Warehouse).code);
      }
      // For other types, use ID comparison
      return selectedItem.id === item.id;
    });
    
    // For product selection, also check if item is in disabled selection (should appear as selected)
    if (this.config?.type === 'product' && this.disabledSelection.length > 0) {
      const isInDisabledSelection = this.disabledSelection.some(disabledItem => disabledItem.id === item.id);
      return isInSelection || isInDisabledSelection;
    }
    
    return isInSelection;
  }

  onCancel(): void {
    this.visibleChange.emit(false);
    this.resetDialog();
  }

  onConfirm(): void {
    this.selectionConfirm.emit(this.selectedItems());
    this.visibleChange.emit(false);
    this.resetDialog();
  }

  private resetDialog(): void {
    this.selectedItems.set([]);
    this.currentPage.set(1);
    
    // Reset search form
    const resetForm: Record<string, string> = {};
    this.config?.searchFields?.forEach(field => {
      resetForm[field.key] = '';
    });
    this.searchForm.set(resetForm);
  }
}
